/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-awesome-styled-grid";
import { Case, Else, If, Switch, Then } from "react-if";
import { Box, FlexContainer, Text, theme } from "@nordcloud/gnui";
import { NoData } from "~/components";
import { Detail } from "~/components/Details/Detail";
import { useDisclosure, useElementNavigation } from "~/hooks";
import { isEmpty, isNil, isNotEmpty, isNotNil, sortByOrder } from "~/tools";
import { ActionBatch } from "~/views/plans/components/ActionBatch";
import { PlanData } from "~/views/plans/PlanCreate/components/PlanCreateWizard/constants";
import { PlanWizardCtxProvider } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanProvider";
import { PlanSettingsForm } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/PlanSettingsForm";
import { ActionItemWrap, WrapMax } from "~/views/plans/styled";
import { ScheduleType } from "~/views/plans/types";
import { getDisplayNameByActionType } from "~/views/plans/utils";
import { DetailPlanEntityName } from "../../components/presets";
import { PlanActionTypeExtended, usePlan } from "../../PlanProvider";
import { ActionItem } from "./ActionItem";
import { ActionBox } from "./components/ActionBox/ActionBox";
import { DetailsTabBar } from "./DetailsTabBar";
import {
  mapPlanActionBatchToPlanEntity,
  mapPlanActionToPlanEntity,
} from "./utils";

export function DetailsTab() {
  const { open, close, isOpen } = useDisclosure();
  const { plan, planAction, setPlanAction } = usePlan();
  const { currentHash, updateHash, scrollToElement } = useElementNavigation();
  const [selectedEntity, setSelectedEntity] = useState<string | undefined>();

  const planActions = useMemo(() => {
    return plan?.planActions ?? [];
  }, [plan]);

  const planActionBatches = useMemo(() => {
    return plan?.planActionBatches ?? [];
  }, [plan]);

  const editablePlanActions = useMemo(() => {
    return planActions.map(mapPlanActionToPlanEntity) ?? [];
  }, [planActions]);

  const editablePlanActionBatches = useMemo(() => {
    return planActionBatches.map(mapPlanActionBatchToPlanEntity) ?? [];
  }, [planActionBatches]);

  const planEntities = sortByOrder<PlanActionTypeExtended>(
    [...editablePlanActions, ...editablePlanActionBatches].filter(isNotNil)
  );

  const [planData, setPlanData] = useState<PlanData>({
    details: undefined,
    schedule_plan: undefined,
    schedule_type: ScheduleType.recurring,
    general_notifications: undefined,
    plan_status: undefined,
    plan_settings: {
      planEntities: planEntities,
    },
  });

  const editedPlanEntities = useMemo(
    () => planData?.plan_settings?.planEntities ?? [],
    [planData]
  );

  const flatEntities = planEntities.flatMap((entity) => {
    if (entity.batchActions) {
      return [entity, ...entity.batchActions];
    }
    return entity;
  });

  const foundEntity = flatEntities.find(
    (entity) => entity.listId === selectedEntity
  );

  const switchEventEntity = useCallback(
    (nextId: string, isBatch = false) => {
      if (isEmpty(nextId) || nextId === selectedEntity) {
        return;
      }
      setSelectedEntity(nextId);
      const newHash = isBatch
        ? { batch: nextId, action: null }
        : { action: nextId, batch: null };
      updateHash(newHash, { merge: true });
    },
    [selectedEntity, setSelectedEntity, updateHash]
  );

  useEffect(() => {
    const { action: hashActionId, batch: hashBatchId, tab } = currentHash || {};

    const hashEntityId = hashActionId || hashBatchId;
    if (hashEntityId && (!tab || tab === "details") && !isOpen) {
      if (
        flatEntities.some((item) => item.id === hashActionId) ||
        planActionBatches.some((batch) => batch.id === hashBatchId)
      ) {
        setSelectedEntity(hashEntityId);
        const targetElementId = hashActionId
          ? `action-${hashActionId}`
          : `batch-${hashBatchId}`;
        scrollToElement(targetElementId);
      } else {
        // invalid id in hash - reset to first item
        setSelectedEntity(flatEntities[0]?.listId);
        updateHash({ action: null, batch: null }, { merge: true });
      }
    }
  }, [
    currentHash,
    flatEntities,
    isOpen,
    planActionBatches,
    setSelectedEntity,
    scrollToElement,
    updateHash,
  ]);

  useEffect(() => {
    if (isNil(foundEntity) && isNil(selectedEntity)) {
      setSelectedEntity(flatEntities[0]?.listId);
    }
    setPlanAction?.(foundEntity);
  }, [flatEntities, foundEntity, selectedEntity, setPlanAction]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const selectedEntityId = selectedEntity ?? "";
    if (isEmpty(selectedEntityId)) {
      updateHash({ action: null, batch: null }, { merge: true });
      return;
    }

    const isSelectedBatch = editedPlanEntities.some(
      (entity) =>
        entity.listId === selectedEntityId &&
        isNotEmpty(entity.batchActions ?? [])
    );

    if (isSelectedBatch) {
      updateHash({ action: null, batch: selectedEntityId }, { merge: true });
      return;
    }

    updateHash({ action: selectedEntityId, batch: null }, { merge: true });
  }, [isOpen, selectedEntity, editedPlanEntities, updateHash]);

  useEffect(() => {
    if (plan && !isOpen) {
      setPlanData((prevData) => ({
        ...prevData,
        plan_settings: {
          ...prevData.plan_settings,
          planEntities: planEntities,
        },
      }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, isOpen]);

  return (
    <PlanWizardCtxProvider
      value={{
        planData,
        setPlanData,
        setSelectedEntity,
        selectedEntity,
      }}
    >
      <Box innerSpacing="spacing00">
        <DetailsTabBar
          openEditMode={open}
          closeEditMode={close}
          isEditMode={isOpen}
        />
        <Switch>
          <Case condition={isOpen}>
            <PlanSettingsForm />
          </Case>
          <Case condition={!isOpen && isNotEmpty(planEntities)}>
            <Row>
              <Col data-testid="actions-list" xs={4} sm={8} md={3} lg={3}>
                <FlexContainer direction="column" justifyContent="flex-start">
                  <Text width="100%" align="left">
                    Actions
                  </Text>
                  {planEntities?.map((entity) => (
                    <If
                      key={entity.listId}
                      condition={isNotEmpty(entity.batchActions ?? [])}
                    >
                      <Then>
                        <WrapMax
                          css={{ paddingTop: `${theme.spacing.spacing03}` }}
                        >
                          <ActionBatch
                            id={entity.listId ?? ""}
                            onClick={() =>
                              switchEventEntity(entity.listId ?? "", true)
                            }
                            active={planAction?.listId === entity.listId}
                            editableName={
                              <Detail
                                detail={DetailPlanEntityName(
                                  {
                                    fallbackName: entity.name,
                                  },
                                  entity.id,
                                  entity.name
                                )}
                                customCss={{
                                  marginBottom: theme.spacing.spacing02,
                                }}
                                editButtonSize="sm"
                              />
                            }
                          >
                            {entity.batchActions?.map((batchAction) => {
                              return (
                                <ActionItemWrap
                                  key={batchAction.listId}
                                  id={`action-${batchAction.listId}`}
                                >
                                  <ActionItem
                                    entity={batchAction}
                                    onMouseDown={() =>
                                      switchEventEntity(
                                        batchAction.listId ?? ""
                                      )
                                    }
                                    renderEditableName={(name: string) => (
                                      <Detail
                                        detail={DetailPlanEntityName(
                                          {
                                            name,
                                            fallbackName:
                                              getDisplayNameByActionType(
                                                batchAction.action?.name ||
                                                  batchAction.actionName ||
                                                  "",
                                                batchAction.actionType
                                              ),
                                            id: batchAction.id ?? "",
                                          },
                                          entity.id
                                        )}
                                        customCss={{ marginBottom: 0 }}
                                        editButtonSize="sm"
                                      />
                                    )}
                                  />
                                </ActionItemWrap>
                              );
                            })}
                          </ActionBatch>
                        </WrapMax>
                      </Then>
                      <Else>
                        <WrapMax>
                          <ActionItemWrap
                            key={entity.listId}
                            id={`action-${entity.listId}`}
                          >
                            <ActionItem
                              entity={entity}
                              onMouseDown={() =>
                                switchEventEntity(entity.listId ?? "")
                              }
                              renderEditableName={(name: string) => (
                                <Detail
                                  detail={DetailPlanEntityName({
                                    name,
                                    fallbackName: getDisplayNameByActionType(
                                      entity.action?.name ||
                                        entity.actionName ||
                                        "",
                                      entity.actionType
                                    ),
                                    id: entity.id ?? "",
                                  })}
                                  customCss={{ marginBottom: 0 }}
                                  editButtonSize="sm"
                                />
                              )}
                            />
                          </ActionItemWrap>
                        </WrapMax>
                      </Else>
                    </If>
                  ))}
                </FlexContainer>
              </Col>
              <Col data-testid="action-details" xs={4} sm={8} md={5} lg={9}>
                <Box boxStyle="grey" innerSpacing="spacing04">
                  <FlexContainer alignItems="flex-start">
                    <ActionBox />
                  </FlexContainer>
                </Box>
              </Col>
            </Row>
          </Case>
          <Case condition={!isOpen && isEmpty(planEntities)}>
            <NoData hasIcon message="There are no Actions" />
          </Case>
        </Switch>
      </Box>
    </PlanWizardCtxProvider>
  );
}
